import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, CheckCircle2, XCircle, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Secure token-based report access page.
 * Used for email links and mobile recovery.
 */
const ReportAccess = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("validating"); // validating, valid, invalid
  const [submissionId, setSubmissionId] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const validateToken = async () => {
      try {
        console.log("[ReportAccess] Validating token...");
        
        const response = await axios.get(`${API_URL}/report/access/${token}`);
        
        if (response.data.status === "valid") {
          setSubmissionId(response.data.submission_id);
          setStatus("valid");
          
          console.log("[ReportAccess] Token valid, redirecting to report...");
          
          // Auto-redirect after brief success display
          setTimeout(() => {
            navigate(`/dba-check/rapport/${response.data.submission_id}`);
          }, 1500);
        } else {
          setStatus("invalid");
          setError("Ongeldige toegangslink");
        }
      } catch (err) {
        console.error("[ReportAccess] Token validation failed:", err);
        setStatus("invalid");
        setError(err.response?.data?.detail || "De toegangslink is ongeldig of verlopen");
      }
    };

    if (token) {
      validateToken();
    } else {
      setStatus("invalid");
      setError("Geen toegangstoken gevonden");
    }
  }, [token, navigate]);

  // Validating state
  if (status === "validating") {
    return (
      <div className="min-h-screen bg-zinc-900 flex flex-col items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="w-20 h-20 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto mb-6">
            <Loader2 className="w-10 h-10 animate-spin text-blue-500" />
          </div>
          <p className="text-white font-medium text-lg mb-2">
            Toegang wordt gecontroleerd...
          </p>
          <p className="text-zinc-500 text-sm">Even geduld alstublieft</p>
        </motion.div>
      </div>
    );
  }

  // Valid state - redirecting
  if (status === "valid") {
    return (
      <div className="min-h-screen bg-zinc-900 flex flex-col items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-emerald-400" />
          </div>
          <p className="text-white font-medium text-lg mb-2">
            Toegang bevestigd!
          </p>
          <p className="text-zinc-400 text-sm mb-6">
            U wordt doorgestuurd naar uw rapport...
          </p>
          <div className="flex items-center justify-center gap-2 text-emerald-400">
            <FileText className="w-5 h-5" />
            <span>Rapport wordt geladen</span>
          </div>
        </motion.div>
      </div>
    );
  }

  // Invalid state
  return (
    <div className="min-h-screen bg-zinc-900 flex flex-col items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center max-w-md"
      >
        <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
          <XCircle className="w-10 h-10 text-red-400" />
        </div>
        <h1 className="text-white font-medium text-xl mb-3">
          Toegang niet mogelijk
        </h1>
        <p className="text-zinc-400 mb-6">
          {error}
        </p>
        <div className="space-y-3">
          <Button
            onClick={() => navigate("/payment-success")}
            className="w-full rounded-full px-6 py-5 bg-gradient-to-r from-[#1DA1F2] to-[#2563EB] text-white"
          >
            Probeer opnieuw
          </Button>
          <Button
            onClick={() => navigate("/dba-check")}
            variant="outline"
            className="w-full rounded-full px-6 py-5 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
          >
            Naar DBA Check
          </Button>
        </div>
        <p className="text-zinc-500 text-sm mt-6">
          Als u betaald heeft, check uw e-mail voor een nieuwe toegangslink.
        </p>
      </motion.div>
    </div>
  );
};

export default ReportAccess;
